import {
    FormStyle,
    TranslationWidth,
    getLocaleDayNames,
} from '@angular/common'
import { Inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core'
import {
    DateComponentUnit,
    EventRecurrence,
    MonthlyRepetitionType,
} from '../domain/Event'

@Pipe({
    name: 'eventRecurrenceDescription',
    standalone: true,
})
export class EventRecurrenceDescriptionPipe implements PipeTransform {

    private readonly dayNames: readonly string[]

    constructor(@Inject(LOCALE_ID) locale: string) {
        this.dayNames = getLocaleDayNames(
            locale,
            FormStyle.Standalone,
            TranslationWidth.Wide
        )
    }

    transform(recurrence: EventRecurrence): string | null {
        const dateComponent = recurrence.dateComponent
        if (dateComponent === null) {
            return null
        }
        if (dateComponent.unit === DateComponentUnit.DAY_OF_WEEK) {
            const repeatsEvery = dateComponent.value === 1
                ? 'Every week'
                : `Every ${dateComponent.value} weeks`
            const days = recurrence.weeklyRepeatsOnDaysOfWeek
                ?.map(day => this.dayNames[day === 7 ? 0 : day]) ?? []
            return days.length > 0
                ? `${repeatsEvery}, on ${days.join(', ')}`
                : repeatsEvery
        }
        if (dateComponent.unit === DateComponentUnit.DAY_OF_MONTH) {
            const repeatsEvery = dateComponent.value === 1
                ? 'Every month'
                : `Every ${dateComponent.value} months`
            const startDate = recurrence.earliestStartDate()
            if (recurrence.monthlyRepetitionType === MonthlyRepetitionType.DayOfMonth) {
                return `${repeatsEvery}, on the ${this.ordinal(startDate.getDate())} day of the month`
            }
            if (recurrence.monthlyRepetitionType === MonthlyRepetitionType.DayOfWeek) {
                const weekOfMonth = Math.floor((startDate.getDate() - 1) / 7) + 1
                return `${repeatsEvery}, each ${this.ordinal(weekOfMonth)} `
                    + `${this.dayNames[startDate.getDay()]} of the month`
            }
            return repeatsEvery
        }
        return null
    }

    private ordinal(value: number): string {
        const lastTwoDigits = value % 100
        if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
            return `${value}th`
        }
        switch (value % 10) {
        case 1:
            return `${value}st`
        case 2:
            return `${value}nd`
        case 3:
            return `${value}rd`
        default:
            return `${value}th`
        }
    }
}
